import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { usePermission } from "../../../features/auth/use-permission";
import { careUnitsQueryOptions } from "../../../features/care-units/api/care-units";
import { HistoryTabs } from "../../../features/change-history/components/history-tabs";
import { RecordHistory } from "../../../features/change-history/components/record-history";
import { historyTabSearchSchema } from "../../../features/change-history/history-search";
import { FacilityChooser } from "../../../features/facility-pick/facility-chooser";
import { directoryQueryOptions } from "../../../features/routing-directory/api/routing-directory";
import { RoutingDirectory } from "../../../features/routing-directory/components/routing-directory";

/**
 * `RPM-105`'s routing directory (`J-487`), with each contact's History, its endpoints' changes
 * beside its own (`RPM-063`, `J-291`).
 *
 * THE ROUTE COMPOSES FEATURES, as `.claude/rules/coding.md` asks: the chooser is `facility-pick`'s,
 * the building's active care units are `care-units`'s list, the directory is `routing-directory`'s
 * and the History tab is `change-history`'s. A unit-scoped contact names one of those care units. The
 * tab is rendered only for an account holding `audit: ["read"]`.
 */
function RoutingDirectoryScreen() {
  const [facilityId, setFacilityId] = useState("");
  const { tab = "details" } = Route.useSearch();
  const navigate = Route.useNavigate();
  const canReadHistory = usePermission({ audit: ["read"] });
  const units = useQuery(careUnitsQueryOptions(facilityId));
  const directory = useQuery({
    ...directoryQueryOptions(facilityId),
    enabled: canReadHistory && facilityId !== "",
  });
  const careUnits = (units.data?.items ?? [])
    .filter((u) => u.isActive)
    .map(({ careUnitId, label }) => ({ careUnitId, label }));
  return (
    <div className="flex flex-col gap-4 pt-4">
      <FacilityChooser value={facilityId} onChange={setFacilityId} />
      {facilityId === "" ? null : (
        <HistoryTabs
          showHistory={canReadHistory}
          tab={tab}
          onTabChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, tab: next }), replace: true })
          }
          detailsLabel="Contacts"
          details={
            <RoutingDirectory key={facilityId} facilityId={facilityId} careUnits={careUnits} />
          }
          history={
            <RecordHistory
              key={facilityId}
              entity="routing-contacts"
              records={(directory.data?.contacts ?? []).map((c) => ({
                id: c.routingContactId,
                label: c.label ?? "Unnamed contact",
              }))}
              noun="Contact"
              caption={(label) => `Change history of contact ${label}, with its endpoints`}
            />
          }
        />
      )}
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/admin/routing-directory")({
  validateSearch: historyTabSearchSchema,
  component: RoutingDirectoryScreen,
  staticData: { screenName: "Contacts and endpoints" },
});
